import type { ArtistIntake } from "./schemas";

export type PaidStatus = "free" | "paid";
export type ContentSensitivity = "standard" | "rights_unclear" | "private_catalog" | "voice_data";

export interface AiRouteRequest {
  analysisConsent: boolean;
  taskType: "dark_room_lite" | "dark_room_full" | "seamline_draft" | "syntha_handoff";
  privacyLevel: ArtistIntake["privacyLevel"];
  paidStatus: PaidStatus;
  maxCostUsd: number;
  structuredOutput: boolean;
  contentSensitivity: ContentSensitivity;
  liveProviderEnabled?: boolean;
  providerAvailable?: boolean;
}

export interface AiCostLedger {
  maxCostUsd: number;
  estimatedCostUsd: number;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  maxInputCharacters: number;
  cacheEligible: boolean;
  evidenceHash?: string;
  freeReportTargetUsd: number;
  accountDailyCapUsd: number;
}

export interface AiRouteDecision {
  allowed: boolean;
  mode: "mock" | "live_disabled" | "live";
  provider: "mock" | "gemini-low-cost" | "openrouter-low-cost" | "privacy-approved-paid";
  reason: string;
  costLedger: AiCostLedger;
  requiredUpgrade?: string;
}

const FREE_REPORT_TARGET_USD = 0.1;
const FREE_ACCOUNT_DAILY_CAP_USD = 0.75;
export const FREE_PREPARATION_INPUT_CHARACTERS = 8000;

export function routeDarkRoomLite(request: AiRouteRequest): AiRouteDecision {
  const costLedger = estimateCost(request);

  if (request.analysisConsent !== true) {
    return blocked(request, costLedger, "Explicit analysis consent is required before evaluation.");
  }

  if (request.contentSensitivity === "voice_data") {
    return blocked(request, costLedger, "Voice data requires explicit consent and is outside SHADOW MVP.");
  }

  if (request.contentSensitivity === "private_catalog" && request.paidStatus === "free") {
    return blocked(request, costLedger, "Private catalog analysis requires paid/private review and consent.", "Trace");
  }

  if (request.contentSensitivity === "rights_unclear") {
    return blocked(request, costLedger, "Rights or artificial intelligence disclosure is incomplete; complete Release Room basics first.");
  }

  if (request.paidStatus === "free") {
    return {
      allowed: true,
      mode: "mock",
      provider: "mock",
      reason: "SHADOW Signal uses local rules with zero paid model egress, regardless of provider availability.",
      costLedger
    };
  }

  if (costLedger.estimatedCostUsd > request.maxCostUsd) {
    return blocked(request, costLedger, "Estimated model cost exceeds request cap.");
  }

  if (request.liveProviderEnabled && request.providerAvailable) {
    return {
      allowed: true,
      mode: "live",
      provider: "privacy-approved-paid",
      reason: "Live provider is enabled, available, under cap, and allowed by sensitivity gates.",
      costLedger
    };
  }

  return {
    allowed: true,
    mode: "mock",
    provider: "mock",
    reason: "Live providers are intentionally disabled until cost ledger, provider privacy review, and rate limits are wired in production.",
    costLedger
  };
}

export function estimateCost(request: AiRouteRequest): AiCostLedger {
  // Free routing is local even when a safety gate rejects the request.
  // Request caps are budgets, not incurred or estimated provider charges.
  if (request.paidStatus === "free") {
    return {
      maxCostUsd: request.maxCostUsd,
      estimatedCostUsd: 0,
      estimatedInputTokens: 0,
      estimatedOutputTokens: 0,
      maxInputCharacters: FREE_PREPARATION_INPUT_CHARACTERS,
      cacheEligible: false,
      freeReportTargetUsd: FREE_REPORT_TARGET_USD,
      accountDailyCapUsd: FREE_ACCOUNT_DAILY_CAP_USD
    };
  }
  const estimatedInputTokens = request.taskType === "dark_room_lite" ? 2400 : 5200;
  const estimatedOutputTokens = request.structuredOutput ? 1200 : 800;
  const cheapStructuredUsdPerMillion = 1.5;
  const estimatedCostUsd = ((estimatedInputTokens + estimatedOutputTokens) / 1_000_000) * cheapStructuredUsdPerMillion;

  return {
    maxCostUsd: request.maxCostUsd,
    estimatedCostUsd: Number(estimatedCostUsd.toFixed(6)),
    estimatedInputTokens,
    estimatedOutputTokens,
    maxInputCharacters: 12000,
    cacheEligible: request.taskType === "dark_room_lite" && request.privacyLevel === "public_only",
    freeReportTargetUsd: FREE_REPORT_TARGET_USD,
    accountDailyCapUsd: FREE_ACCOUNT_DAILY_CAP_USD
  };
}

export function normalizeEvidenceForCache(input: string): string {
  return input.toLowerCase().replace(/\s+/g, " ").trim().slice(0, 8000);
}

export function evidenceCacheKey(input: string): string {
  const normalized = normalizeEvidenceForCache(input);
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = (hash << 5) - hash + normalized.charCodeAt(i);
    hash |= 0;
  }
  return `evidence_${Math.abs(hash).toString(36)}`;
}

function blocked(
  request: AiRouteRequest,
  costLedger: AiCostLedger,
  reason: string,
  requiredUpgrade?: string
): AiRouteDecision {
  return {
    allowed: false,
    mode: "live_disabled",
    provider: request.paidStatus === "paid" ? "privacy-approved-paid" : "mock",
    reason,
    costLedger,
    requiredUpgrade
  };
}
